/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { SearchOutlined } from '@ant-design/icons'
import { Col, Row, Space, Input, Badge, Table } from 'antd'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { selectCurrentUser } from 'Store/user/selector'
import { TableListPage } from 'Components/common/Table'
import { PrimaryButton } from 'Components/button'
import HomePageLayout from 'Pages/Layout/PageLayout'
import Detail from './detail'
import { Pagination } from 'Components/Pagination'
import { MyTheme } from 'Components/common/Theme'
import LinkText from 'Components/text/LinkText'
import { getPosMornitoringListAPI } from 'Store/pos-mornitoring/api'
import { ListReq, Data } from 'Store/pos-mornitoring/types'
import { selectList, selectListEvent } from 'Store/pos-mornitoring/selector'
import { formatDateTime, formatTimeAgo } from 'helpers/format'

type Props = {} & ReduxProps

const PosMornitoring: React.FunctionComponent<Props> = props => {
  const { currentUser, list, event } = props
  const dispatch = useDispatch()
  const [drawer, setDrawer] = useState(false)
  const [selectedID, setSelectedID] = useState(0)

  const [filter, setFilter] = useState({
    keyword: '',
  })
  const [pagination, setPagination] = useState({
    Page: 1,
    RowPerPage: 10,
  })
  const meta = list.meta
  const datas = list.data

  const onGetList = (): void => {
    const req: ListReq = {
      page: pagination.Page,
      per_page: pagination.RowPerPage,
      name: filter.keyword,
    }
    dispatch(getPosMornitoringListAPI(req, currentUser.accessToken))
  }

  useEffect(() => {
    if (currentUser?.accessToken) {
      onGetList()
    }
  }, [pagination])

  const onClickSearch = () => {
    pagination.Page = 1
    setPagination({ ...pagination })
  }

  const onClickDetail = (id: number) => {
    setSelectedID(id)
    setDrawer(true)
  }

  useEffect(() => {
    if (pagination.Page === 1) return
    pagination.Page = 1
    pagination.RowPerPage = 10
    setPagination({ ...pagination })
  }, [])

  const onFilter = (key: string, val: string) => {
    setFilter({
      ...filter,
      [key]: val,
    })
  }

  const onCloseDrawer = () => {
    setDrawer(false)
  }

  const onSuccess = () => {
    onCloseDrawer()
    onGetList()
  }

  const expandedRowRender = (record: Data) => {
    const terminals = record?.package?.terminals || []
    const columns = [
      {
        title: 'ลำดับ',
        dataIndex: 'no',
        key: 'no',
        render: (value: any, record: any, index: number) => {
          return index + 1
        },
      },
      { title: 'Terminal Name', dataIndex: 'name', key: 'name' },
      { title: 'Serial No', dataIndex: 'serial_no', key: 'serial_no' },
      {
        title: 'สถานะเครื่อง',
        key: 'online',
        dataIndex: 'online',
        render: (online: boolean) => (
          <span>
            <Badge status={online ? 'success' : 'error'} />
            {online ? 'ออนไลน์' : 'ออฟไลน์'}
          </span>
        ),
      },
      {
        title: 'เคลื่อนไหวล่าสุด',
        dataIndex: 'lasted_at',
        key: 'lasted_at',
        render: (val: any) => formatTimeAgo(val || ''),
      },
      { title: 'IP Address ของเครื่อง', dataIndex: 'ip', key: 'ip' },
    ]

    return (
      <InnerTable>
        <Table columns={columns} dataSource={terminals} pagination={false} size="small"></Table>
      </InnerTable>
    )
  }

  const expandedContent = {
    expandedRowRender: expandedRowRender,
    fixed: 'left',
    expandIconColumnIndex: 0,
    defaultExpandAllRows: false,
  }

  const columns = [
    {
      title: 'ชื่อร้าน',
      dataIndex: 'merchant',
      key: 'merchant_name',
      render: (merchant: any, record: any) => (
        <MerchantName onClick={() => onClickDetail(record.id)}>{merchant.name}</MerchantName>
      ),
    },
    {
      title: 'รหัสร้านค้า',
      dataIndex: 'merchant',
      key: 'merchant_id',
      render: (merchant: any) => merchant?.id || 0,
    },
    {
      title: 'แพ็กเกจ',
      dataIndex: 'package',
      key: 'package_name',
      render: (val: any) => val?.name || '',
    },
    {
      title: 'วันหมดอายุแพ็กเกจ',
      dataIndex: 'package',
      key: 'package_date',
      render: (val: any) => formatDateTime(val?.end_date || ''),
    },
    {
      title: 'จำนวนเครื่อง',
      key: 'package_terminal',
      dataIndex: 'package',
      width: '150px',
      render: (val: any) => (val?.terminals && val?.terminals.length) || 0,
    },
  ]

  const DetailDrawer = (
    <Detail id={selectedID} visible={drawer} onCloseDrawer={onCloseDrawer} onSuccess={onSuccess} />
  )

  const FilterContent = (
    <FilterWrapper>
      <Row justify="space-between" align="bottom">
        <Col xs={24} md={12}>
          <Space size="middle" align="end">
            <SearchInput
              placeholder="ค้นหาชื่อร้านค้า"
              prefix={<SearchOutlined />}
              allowClear
              value={filter.keyword}
              onChange={(evt: any): void => onFilter('keyword', evt.target.value)}
            />
            <PrimaryButton text="ค้นหา" btnType="Cancel" onClick={onClickSearch} />
          </Space>
        </Col>
      </Row>
    </FilterWrapper>
  )

  const PaginationContent = (
    <Pagination
      currentPage={pagination.Page}
      totalRows={meta?.totalCount || 0}
      perPage={pagination.RowPerPage}
      onClickPage={(page: number) => {
        pagination.Page = page
        setPagination({ ...pagination })
      }}
      onChangeNumber={(number: number) => {
        pagination.RowPerPage = number
        setPagination({ ...pagination })
      }}
    />
  )

  return (
    <>
      <HomePageLayout selected="pos-mornitoring" title="ร้านค้าที่ใช้งานระบบขายหน้าร้าน (Terminal)">
        {FilterContent}
        <TableListPage
          columns={columns}
          datas={datas}
          isLoading={event.IsLoading}
          paginationContent={PaginationContent}
          expandable={expandedContent}
          scrollX={500}
        />
        {DetailDrawer}
      </HomePageLayout>
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    list: selectList(state),
    event: selectListEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

const FilterWrapper = styled.div`
  padding: 10px 0 5px 0;
`

const SearchInput = styled(Input)`
  border-radius: 6px;
  height: 45px;
`

const MainText = styled(LinkText)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  text-decoration: none;
  cursor: default;
`

const MerchantName = styled(MainText)`
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  cursor: pointer;
`

const InnerTable = styled.div`
  padding: 10px 0;
  th {
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
    color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey} !important;
  }

  td {
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  }
`

export default connect(mapStateToProps)(PosMornitoring)
